import classNames from "classnames";
import classes from "./B2CCartProcessTile.module.less";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { PopperTooltip } from "~/common/components/Tooltip";
import { Link } from "~/common/components/Link";
import { CART_PAGE, CART_STEP_2 } from "~/utils/url/staticUrls";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ShoppingCartCheckoutOutlinedIcon from '@mui/icons-material/ShoppingCartCheckoutOutlined';

interface Props {
  className?: string;
  activeIndex?: number;
}

interface StepDef {
  nameKey: string;
  icon: ReactNode;
  activeIcon: ReactNode;
  href?: string;
}

const steps: StepDef[] = [
  {
    nameKey: "cart.b2c.checkout.step.1",
    icon: <ShoppingCartOutlinedIcon />,
    activeIcon: <ShoppingCartOutlinedIcon />,
    href: CART_PAGE,
  },
  {
    nameKey: "cart.b2c.checkout.step.2",
    icon: <FmdGoodOutlinedIcon />,
    activeIcon: <FmdGoodOutlinedIcon />,
    href: CART_STEP_2,
  },
  {
    nameKey: "cart.b2c.checkout.step.3",
    icon: <ShoppingCartCheckoutOutlinedIcon />,
    activeIcon: <ShoppingCartCheckoutOutlinedIcon />,
  },
];

export const B2CCartProcessTile = ({ className, activeIndex = -1 }: Props) => {
  const makeStep = (index: number) => (
    <SingleStep key={index} step={steps[index]} active={activeIndex === index} clickable={index < activeIndex} />
  );

  const elements = [makeStep(0)];
  for (let i = 1; i < steps.length; i++) {
    elements.push(<Divider active={activeIndex >= i} key={`divider-${i}`} />);
    elements.push(makeStep(i));
  }
  return <div className={classNames(classes.root, className)}>{elements}</div>;
};

const SingleStep = ({ step, active, clickable }: { step: StepDef; active?: boolean; clickable?: boolean }) => {
  const { t } = useTranslation();
  const label = t(step.nameKey);
  const maxLabelLength = useIsMinTabletSize() ? 40 : 35;
  let labelElement;
  if (label.length <= maxLabelLength) {
    labelElement = <span>{label}</span>;
  } else {
    labelElement = (
      <PopperTooltip tooltipContent={label} placement={"bottom"}>
        <div>{label.substring(0, maxLabelLength) + "..."}</div>
      </PopperTooltip>
    );
  }
  return (
    <Link
      disabled={!clickable}
      href={step.href}
      className={classNames(
        classes.step,
        active && classes.stepActive,
        clickable ? classes.stepClickable : classes.stepDisabled,
      )}
    >
      <div className={classes.stepIcon}>{active || clickable ? step.activeIcon : step.icon}</div>
      <div className={classes.stepName}>{labelElement}</div>
    </Link>
  );
};

const Divider = (props: { active?: boolean }) => {
  return <div className={classNames(classes.divider, props.active && classes.active)} />;
};
